import { useState, useEffect, useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import { IUser, IPosition, ISimpleUser } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { PositionBadge } from "../../CrewSchedule/PositionBadge";
import { formatUserLabel } from "src/components/Admin/Tabs/Users/ManageUsers";
// Shared with ManageCrew, NFAEditorWindow and CrewLedger. This file used to carry its own copy,
// which meant the ENG-440 positions showed "(0)" here and trainers stayed under Flight Attendants.
import { formatCrewList } from "src/components/Admin/Tabs/Crew/ManageCrew";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
import { formatFunction, parseFunction } from "./helpers";
import { sortByDisplayOrder } from "src/utils";

interface ICrewSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
}

// Types for crew retrieval aren't really full IUser but rather a reduced interface containing...
interface IBaseUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Sepcialized wrapper for "built-in" oc crew select field.
 * @param props -
 */
export const CrewSelectField = (props: ICrewSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  //const mosaicPane = useMemo(() => {
  //  return id ? document.getElementById(`${id}-panel`) : null;
  //}, [id]);

  /////////////////////////
  // MANAGE CREW LIST
  /////////////////////////
  const [users, setUsers] = useState<ISimpleUser[]>([]);
  const [groupedCrewList, setGroupedCrewList] = useState<any>([]);

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<ISimpleUser>("/users?simpleUser=true");
      if (Array.isArray(response)) {
        setUsers(response);
      }
    };
    getUsersAction();
  }, []);

  // Available Positions (Used to sort Crew Groups in master list and in the form).
  // Copy before sorting - this is the redux-owned master position list that every position selector
  // reads from, so sorting it directly reorders shared state as a side effect of rendering.
  // Memoised because `positions` feeds the effect below, which sets state.
  const rawPositions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const positions: IPosition[] = useMemo(() => [...rawPositions].sort(sortByDisplayOrder), [rawPositions]);

  // If positions are users are updated, rebuilt crew list.
  useEffect(() => {
    setGroupedCrewList(formatCrewList(users, positions));
  }, [positions, users]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, users, valueKey, isMulti),
    [users, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Crew Selector"}
      isMulti={isMulti}
      options={groupedCrewList}
      getOptionLabel={formatUserLabel}
      formatOptionLabel={(user: IUser) => {
        const { firstName, lastName, employeeId, crewInfo } = user;
        const highPos = crewInfo?.positions?.sort((a, b) => a.displayOrder - b.displayOrder)[0];
        return (
          <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
            {highPos && <PositionBadge position={highPos} filled={true} isInactive={!user?.active} />}
            <Grid item style={{ marginLeft: 10 }}>{`${firstName} ${lastName} ${
              employeeId ? `[${employeeId}]` : ""
            }`}</Grid>
          </Grid>
        );
      }}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      getOptionValue={(user: IUser) => user._id}
      placeholder="Select Crew"
      isSearchable={true}
      isClearable={true}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
